import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { HranaPickerDialog } from "@/components/dielec/HranyEditor";
import { SlotEdgePickerDialog } from "@/components/dielec/SlotEdgePickerDialog";
import { useMaterialDetail } from "@/api/hooks/useCennik";
import { useUpdateDielec } from "@/api/hooks/useDielce";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { materialPrimaryId, normalizeNum, parseDecimal } from "@/lib/format";
import type { Dielec, MaterialSummary, PridanaHranaInput } from "@/api/schemas";

/**
 * Pridané hrany dielca — olepenie otvoru a pod. (KOLO 169).
 *
 * User: *„vnútorné olepenie otvoru dielca, vtedy treba tiež dať pridanú
 * hranu k dielcu"*. Riadok = hrana z cenníka, dĺžka jedného behu, počet
 * behov na kus a popis („otvor"). Cena aj objednávka ako strana dielca —
 * nadmieru pásky pripočíta appka.
 *
 * Okno, nie sekcia v detaile: karta detailu má pevný výškový rozpočet
 * (KOLO 66/100, stĺpce `overflow-hidden`) a zoznam riadkov by sa do nej
 * nezmestil — orezal by sa práve posledný riadok. Uloží sa jedným
 * PATCH-om s celým zoznamom (backend ho nahrádza celý).
 */
interface Riadok {
  kluc: string;
  material_id: string;
  material?: MaterialSummary | null;
  dlzka: string;
  pocet: string;
  popis: string;
}

function naRiadky(d: Dielec): Riadok[] {
  return (d.pridane_hrany ?? [])
    .filter((h) => h.material_id)
    .map((h, i) => ({
      kluc: h.id ?? `r${i}`,
      material_id: h.material_id as string,
      material: h.material ?? null,
      dlzka: normalizeNum(h.dlzka_mm),
      pocet: String(h.pocet ?? 1),
      popis: h.popis ?? "",
    }));
}

/** Riadky okna v tvare požiadavky; `null` = niektorý nemá dĺžku alebo počet. */
function naVstup(riadky: Riadok[]): PridanaHranaInput[] | null {
  const vystup: PridanaHranaInput[] = [];
  for (const r of riadky) {
    const dlzka = parseDecimal(r.dlzka);
    const pocet = parseInt(r.pocet, 10);
    if (!(parseFloat(dlzka) > 0) || !(pocet >= 1)) return null;
    vystup.push({
      material_id: r.material_id,
      dlzka_mm: dlzka,
      pocet,
      popis: r.popis.trim(),
    });
  }
  return vystup;
}

/**
 * Z čoho ponúknuť hrany (KOLO 196). User: *„nech mi dáva na výber hrany,
 * ktoré používame, nie že mám hľadať hranu"* — ten istý výber ako pri
 * strane dielca: hrany zákazky (navrchu tie zo strán dielca), detegované
 * pre materiál dosky, celý cenník až pod nimi.
 */
export interface VyberPridanejHrany {
  materialId: string;
  slotId?: string;
  variantaId?: string;
  hrubaHranaMm?: string;
  zMm?: number;
  naDielci: string[];
}

export function PridaneHranyDialog({
  zakazkaId,
  dielec,
  open,
  onOpenChange,
  vyber,
}: {
  zakazkaId: string;
  dielec: Dielec;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Bez materiálu dosky ostáva hľadanie v sekcii Hrany. */
  vyber?: VyberPridanejHrany | null;
}): JSX.Element {
  const updateMutation = useUpdateDielec(zakazkaId);
  const [riadky, setRiadky] = useState<Riadok[]>(() => naRiadky(dielec));
  const [picker, setPicker] = useState(false);
  const [dalsi, setDalsi] = useState(0);

  useEffect(() => {
    if (open) setRiadky(naRiadky(dielec));
    // Pri otvorení stav z dielca; počas úprav refetch rozpísané neprepíše
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function pridaj(id: string): void {
    setRiadky((rs) => [
      ...rs,
      { kluc: `novy${dalsi}`, material_id: id, dlzka: "", pocet: "1", popis: "" },
    ]);
    setDalsi((n) => n + 1);
    setPicker(false);
  }

  function zmen(kluc: string, zmena: Partial<Riadok>): void {
    setRiadky((rs) => rs.map((r) => (r.kluc === kluc ? { ...r, ...zmena } : r)));
  }

  async function uloz(): Promise<void> {
    const vstup = naVstup(riadky);
    if (vstup === null) {
      toast({
        variant: "destructive",
        title: "Doplň dĺžku a počet",
        description: "Každá pridaná hrana potrebuje dĺžku (mm) a počet aspoň 1.",
      });
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: dielec.id,
        data: { pridane_hrany: vstup },
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Pridané hrany — {dielec.kod}</DialogTitle>
            <DialogDescription>
              Hrana mimo obvodu dielca, napr. olepenie otvoru. Dĺžka je jeden beh
              bez nadmiery — tú pripočíta appka ako pri strane.
            </DialogDescription>
          </DialogHeader>
          {riadky.length === 0 ? (
            <p className="text-sm text-muted-foreground">Dielec nemá pridané hrany.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase text-muted-foreground">
                  <th className="py-1 font-medium">Hrana</th>
                  <th className="w-24 py-1 font-medium">Dĺžka (mm)</th>
                  <th className="w-16 py-1 font-medium">Počet</th>
                  <th className="w-40 py-1 font-medium">Popis</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {riadky.map((r) => (
                  <tr key={r.kluc} className="align-middle">
                    <td className="py-1 pr-2">
                      <NazovHrany materialId={r.material_id} material={r.material} />
                    </td>
                    <td className="py-1 pr-2">
                      <Input
                        aria-label="Dĺžka pridanej hrany (mm)"
                        value={r.dlzka}
                        onChange={(e) => zmen(r.kluc, { dlzka: e.target.value })}
                        inputMode="decimal"
                        className="h-7 font-mono text-xs"
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <Input
                        aria-label="Počet pridanej hrany"
                        value={r.pocet}
                        onChange={(e) => zmen(r.kluc, { pocet: e.target.value })}
                        inputMode="numeric"
                        className="h-7 font-mono text-xs"
                      />
                    </td>
                    <td className="py-1 pr-2">
                      <Input
                        aria-label="Popis pridanej hrany"
                        value={r.popis}
                        onChange={(e) => zmen(r.kluc, { popis: e.target.value })}
                        placeholder="napr. otvor"
                        maxLength={100}
                        className="h-7 text-xs"
                      />
                    </td>
                    <td className="py-1">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 hover:text-destructive"
                        aria-label="Odobrať pridanú hranu"
                        onClick={() =>
                          setRiadky((rs) => rs.filter((x) => x.kluc !== r.kluc))
                        }
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div>
            <Button type="button" size="sm" variant="outline" onClick={() => setPicker(true)}>
              + Pridať hranu
            </Button>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Zrušiť
            </Button>
            <Button
              type="button"
              onClick={() => void uloz()}
              disabled={updateMutation.isPending}
            >
              Uložiť
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Výber hrany je samostatné okno nad týmto — ten istý ako pri strane
          dielca (KOLO 196): najprv hrany zákazky, bez materiálu dosky len
          hľadanie v sekcii Hrany */}
      {vyber ? (
        <SlotEdgePickerDialog
          open={picker}
          nadpis="Vybrať pridanú hranu"
          slotMaterialId={vyber.materialId}
          slotId={vyber.slotId}
          variantaId={vyber.variantaId}
          hrubaHranaMm={vyber.hrubaHranaMm}
          zMm={vyber.zMm}
          naDielci={vyber.naDielci}
          bezKuracie
          currentId={null}
          onClose={() => setPicker(false)}
          onSelect={pridaj}
        />
      ) : (
        <HranaPickerDialog
          open={picker}
          currentId={null}
          onClose={() => setPicker(false)}
          onSelect={pridaj}
        />
      )}
    </>
  );
}

/** Kód a názov hrany — z odpovede, pri novom riadku z detailu cenníka. */
function NazovHrany({
  materialId,
  material,
}: {
  materialId: string;
  material?: MaterialSummary | null;
}): JSX.Element {
  const detail = useMaterialDetail(material ? undefined : materialId);
  const m = material ?? detail.data?.data ?? null;
  if (!m) return <span className="text-xs text-muted-foreground">Načítava sa…</span>;
  const kod = materialPrimaryId(m);
  return (
    <span className="block max-w-[16rem] truncate text-xs" title={m.nazov}>
      {kod ? <span className="font-mono">{kod} · </span> : null}
      {m.nazov}
    </span>
  );
}
